import React from "react";
import dayjs, { Dayjs } from "dayjs";

import { Typography, List, ListItem, IconButton, Tooltip, Button, Chip } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import TimelineIcon from "@mui/icons-material/Timeline";
import SavingsIcon from "@mui/icons-material/Savings";
import EuroIcon from "@mui/icons-material/Euro";
import InsightsIcon from "@mui/icons-material/Insights";

import ErrorDialogue from "../../Components/ErrorDialogue/ErrorDialogue";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import YearlyBalanceChart from "../../Components/Graphs/YearlyBalanceChart";
import SixMonthGotExpectedChart from "../../Components/Graphs/SixMonthGotExpectedChart";
import Transaction from "../../Types/Transaction";
import Search from "../../Components/Search/Search";
import Enumerable from "linq";
import Spinner from "../../Components/Spinner/Spinner";
import TransactionRepository from "../../Repositories/TransactionRepository";
import BalanceStateRepository from "../../Repositories/BalanceStateRepository";
import AddMoneyDialog from "../../Components/Dialogs/AddMoneyDialog";
import WithdrawMoneyDialog from "../../Components/Dialogs/WithrawMoneyDialog";
import CountPeriod from "../../Types/CountPeriod";
import CountPeriodRepository from "../../Repositories/CountPeriodRepository";
import { CountPeriodType } from "../../Types/Enums/CountPeriodType";

type ExpectedMonth = { year: number; month: number; expected: number };

interface TipJarPageStates {
  errorTitle?: string;
  errorMsg?: string;

  isLoading: boolean;

  transactions: Transaction[];
  monthlyExpected: ExpectedMonth[];
  balance: number;

  selectedMonth: Dayjs | null;
  search: string;

  anchorElement: HTMLElement | null;
  selectedTx: Transaction | null;

  showAddDialog: boolean;
  showSubtractDialog: boolean;
}

export default class TipJarPage extends React.Component<{}, TipJarPageStates> {
  static contextType = AuthContext;
  context!: AuthContextType;

  private accent = "#c79656";

  constructor(props: any) {
    super(props);

    this.formatToEuro = this.formatToEuro.bind(this);
    this.getBalance = this.getBalance.bind(this);
    this.getTipTransactionsForPeriod = this.getTipTransactionsForPeriod.bind(this);
    this.deleteTransaction = this.deleteTransaction.bind(this);
    this.refresh = this.refresh.bind(this);
    this.handleAddTransaction = this.handleAddTransaction.bind(this);
    this.handleWithdrawTransaction = this.handleWithdrawTransaction.bind(this);

    this.state = {
      errorTitle: "",
      errorMsg: "",

      isLoading: true,

      transactions: [],
      monthlyExpected: [],
      balance: 0,

      selectedMonth: dayjs(),
      search: "",

      anchorElement: null,
      selectedTx: null,

      showAddDialog: false,
      showSubtractDialog: false,
    };
  }

  private async getTipTransactionsForPeriod() {
    const result = await TransactionRepository.getAllTransactions(
      this.context.locationId,
      dayjs().subtract(5, "month").startOf("month"),
      dayjs()
    );

    if (result.isFailed()) {
      this.setState({ errorMsg: result.errorMessage ?? "Something went wrong", errorTitle: "An error occured!" });
      return;
    }

    const transactions = result.result!.transactions.map(
      (x) =>
        ({
          id: x.id,
          locationId: x.locationId,
          balanceStateId: x.balanceStateId,
          amount: x.amount,
          description: x.description,
          createdBy: x.createdBy,
          createdOn: x.createdOn,
          createdByName: x.createdByName,
        }) as Transaction
    );

    this.setState({
      transactions: transactions.sort(
        (a, b) =>
          -Number(dayjs(a.createdOn).format("YYYYMMDDHHmmss")) + Number(dayjs(b.createdOn).format("YYYYMMDDHHmmss"))
      ),
    });
  }

  private async getBalance() {
    const result = await BalanceStateRepository.getLatestBalanceStateById(this.context.locationId);

    if (result.isFailed()) {
      this.setState({ errorMsg: result.errorMessage ?? "Something went wrong", errorTitle: "An error occured!" });
      return;
    }

    this.setState({ balance: result.result!.balanceState.newBalance });
  }

  private async refresh() {
    this.setState({ isLoading: true });
    await Promise.all([this.getBalance(), this.getTipTransactionsForPeriod()]);
    this.setState({ isLoading: false });
  }

  private async deleteTransaction(tx: Transaction) {}

  public componentDidMount() {
    this.refresh();
  }

  private formatToEuro(n: number) {
    return `€${(n || 0).toFixed(2)}`;
  }

  private formatCurrency(n: number) {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
  }

  private async handleAddTransaction(amount: number, note: string) {
    const description = note?.trim() || "Deposit";
    this.setState({ isLoading: true });
    const result = await TransactionRepository.addTransaction(this.context.locationId, amount, description);
    if (result.isFailed()) {
      this.setState({
        isLoading: false,
        errorTitle: result.errorTitle ?? "An error occured!",
        errorMsg: result.errorMessage ?? "Something went wrong",
      });
      return;
    }
    this.setState({ showAddDialog: false });
    await this.refresh();
  }

  private async handleWithdrawTransaction(amount: number, reason: string) {
    const description = reason.trim();
    const negativeAmount = -Math.abs(amount);
    this.setState({ isLoading: true });
    const result = await TransactionRepository.addTransaction(this.context.locationId, negativeAmount, description);
    if (result.isFailed()) {
      this.setState({
        isLoading: false,
        errorTitle: result.errorTitle ?? "An error occured!",
        errorMsg: result.errorMessage ?? "Something went wrong",
      });
      return;
    }
    this.setState({ showSubtractDialog: false });
    await this.refresh();
  }

  private KpiTile({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
    const accent = this.accent;

    return (
      <div
        role="region"
        aria-label={label}
        style={{
          background: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          padding: "10px 12px",
          minHeight: "80px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxSizing: "border-box",
          borderTop: `3px solid ${accent}`,
          transition: "box-shadow 160ms ease, transform 160ms ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 8px 22px rgba(0,0,0,0.08)";
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)";
          (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            aria-hidden
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: `${accent}14`,
              border: `1px solid ${accent}33`,
              display: "grid",
              placeItems: "center",
            }}
          >
            <div style={{ color: accent }}>{icon}</div>
          </div>

          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              color: "rgba(26,26,26,0.65)",
              userSelect: "none",
              fontWeight: 600,
            }}
          >
            {label}
          </div>
        </div>

        <div
          style={{
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: 0.2,
            fontVariantNumeric: "tabular-nums",
            background: `linear-gradient(180deg, #1f2937 0%, #1f2937 40%, ${accent} 220%)`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            textShadow: "0 0 1px rgba(0,0,0,0.03)",
          }}
        >
          {value}
        </div>
      </div>
    );
  }

  private PaperCard(opts: {
    title: string;
    minHeight?: number;
    spanCols?: number;
    spanRows?: number;
    children?: React.ReactNode;
    headerRight?: React.ReactNode;
  }) {
    const { title, minHeight = 100, spanCols = 1, spanRows = 1, children, headerRight } = opts;

    return (
      <div
        role="region"
        aria-label={title}
        style={{
          background: "#FFFFFF",
          borderRadius: "16px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
          padding: "16px 8px 8px 8px",
          boxSizing: "border-box",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          minHeight: `${minHeight}px`,
          gridColumn: `span ${spanCols}`,
          gridRow: `span ${spanRows}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, opacity: 0.9 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: this.accent, display: "inline-block" }} />
            {title}
          </div>
          {headerRight}
        </div>

        <div style={{ flex: 1, minHeight: 80, display: "flex", alignItems: "stretch", justifyContent: "stretch" }}>
          {children ? <div style={{ width: "100%", height: "100%", display: "flex" }}>{children}</div> : null}
        </div>
      </div>
    );
  }

  public render() {
    const last30dFrom = dayjs().subtract(30, "day");
    const net30d = Enumerable.from(this.state.transactions || []).where((x) => dayjs(x.createdOn).isAfter(last30dFrom)).sum((x) => x.amount) ?? 0;
    const deposits30d =
      Enumerable.from(this.state.transactions || []).where((x) => dayjs(x.createdOn).isAfter(last30dFrom) && x.amount > 0).sum((x) => x.amount) ?? 0;
    const withdrawals30d =
      Enumerable.from(this.state.transactions || []).where((x) => dayjs(x.createdOn).isAfter(last30dFrom) && x.amount < 0).sum((x) => Math.abs(x.amount)) ?? 0;

    return (
      <div style={{ width: "100%", height: "100%" }}>
        <ErrorDialogue
          show={Boolean(this.state.errorMsg)}
          title={this.state.errorTitle ?? ""}
          message={this.state.errorMsg ?? ""}
          onClose={() => this.setState({ errorTitle: undefined, errorMsg: undefined })}
        />

        <div
          style={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            padding: 16,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {this.state.isLoading && <Spinner />}

          {/* Header: title left, actions right */}
          <div
            style={{
              width: "100%",
              maxWidth: 1600,
              margin: "0 auto",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {/* LEFT: Accent + Title */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={{ width: 6, height: 36, background: this.accent, borderRadius: 6 }} />
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  color: "#1a1a1a",
                  letterSpacing: "0.2px",
                  whiteSpace: "nowrap",
                }}
              >
                Tip jar
              </div>
            </div>

            {/* RIGHT: Actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => this.setState({ showAddDialog: true })}
                sx={{ borderRadius: 2, backgroundColor: this.accent, "&:hover": { backgroundColor: "#b78445" }, textTransform: "none", fontWeight: 700 }}
              >
                Deposit
              </Button>

              <Button
                variant="outlined"
                startIcon={<RemoveCircleOutlineIcon />}
                onClick={() => this.setState({ showSubtractDialog: true })}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: `${this.accent}66`,
                  color: "#1a1a1a",
                  "&:hover": { borderColor: this.accent, background: `${this.accent}10` },
                }}
              >
                Withdraw
              </Button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", boxSizing: "border-box", paddingBottom: 16 }}>
            <div style={{ width: "100%", maxWidth: 1600, margin: "0 auto", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, width: "100%", boxSizing: "border-box" }}>
                {this.KpiTile({ label: "Current balance", value: this.formatCurrency(this.state.balance ?? 0), icon: <SavingsIcon fontSize="small" /> })}
                {this.KpiTile({ label: "Net last 30 days", value: this.formatCurrency(net30d), icon: <InsightsIcon fontSize="small" /> })}
                {this.KpiTile({ label: "Deposits last 30 days", value: this.formatCurrency(deposits30d), icon: <EuroIcon fontSize="small" /> })}
                {this.KpiTile({ label: "Withdrawals last 30 days", value: this.formatCurrency(withdrawals30d), icon: <TimelineIcon fontSize="small" /> })}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, width: "100%", boxSizing: "border-box" }}>
                {this.PaperCard({ title: "Balance overview", minHeight: 200, children: <YearlyBalanceChart height={200} /> })}
                {this.PaperCard({ title: "Received vs. calculated tips", minHeight: 200, children: <SixMonthGotExpectedChart height={200} /> })}
              </div>

              {this.PaperCard({
                title: "Transactions",
                minHeight: 520,
                headerRight: (
                  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ width: 420, maxWidth: "65vw" }}>
                      <Search sx={{width: "100%", height: "30px"}} onChange={(value) => this.setState({ search: value })} />
                    </div>
                  </div>
                ),
                children: (
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <div
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backdropFilter: "saturate(180%) blur(8px)",
                        background: "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.78))",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        display: "grid",
                        gridTemplateColumns: "120px 180px 140px 1fr 52px",
                        alignItems: "center",
                        padding: "10px 12px",
                        height: 44,
                        boxSizing: "border-box",
                        borderRadius: 12,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        User
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <div />
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflow: "auto", marginTop: 8 }}>
                      <List style={{ margin: 0, padding: 0 }}>
                        {this.state.transactions
                          ?.filter((t) => {
                            const q = (this.state.search || "").trim().toLowerCase();
                            if (!q) return true;
                            return (
                              dayjs(t.createdOn).format("DD-MM-YYYY").includes(q) ||
                              (t.createdByName || "").toLowerCase().includes(q) ||
                              this.formatToEuro(t.amount).toLowerCase().includes(q) ||
                              (t.description || "").toLowerCase().includes(q)
                            );
                          })
                          .map((transaction, index) => (
                            <ListItem
                              key={transaction.id}
                              sx={{
                                padding: 0,
                                "& > div.row": {
                                  display: "grid",
                                  gridTemplateColumns: "120px 180px 140px 1fr 52px",
                                  alignItems: "center",
                                  padding: "12px 12px",
                                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                                  backgroundColor: index % 2 === 0 ? "#ffffff" : "rgba(0,0,0,0.012)",
                                  borderRadius: 10,
                                  transition: "background 180ms ease",
                                  columnGap: 8,
                                },
                                "&:hover > div.row": { backgroundColor: `${this.accent}10` },
                                "& .row-actions": { opacity: 0, pointerEvents: "none", transition: "opacity 160ms ease" },
                                "&:hover .row-actions": { opacity: 1, pointerEvents: "auto" },
                              }}
                            >
                              <div className="row" style={{ width: "100%" }}>
                                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                  {dayjs(transaction.createdOn).format("DD-MM-YYYY")}
                                </Typography>

                                <Chip
                                  size="small"
                                  sx={{
                                    width: 160,
                                    fontSize: "0.75rem",
                                    borderRadius: 2,
                                    background: `${this.accent}14`,
                                    border: `1px solid ${this.accent}33`,
                                    ".MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                                  }}
                                  label={transaction.createdByName}
                                />

                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    fontVariantNumeric: "tabular-nums",
                                    color: transaction.amount > 0 ? "#12a037ff" : "#c52626ff",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={`Amount: ${this.formatToEuro(transaction.amount)}`}
                                >
                                  {this.formatToEuro(transaction.amount)}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "rgba(0,0,0,0.72)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    paddingRight: 8,
                                  }}
                                  title={transaction.description ?? ""}
                                >
                                  {transaction.description ?? "—"}
                                </Typography>

                                <div className="row-actions" style={{ gridColumn: 5, justifySelf: "end", display: "flex", gap: 4 }}>
                                  <Tooltip title={"Delete"}>
                                    <IconButton size="small" onClick={() => this.deleteTransaction(transaction)}>
                                      <DeleteIcon fontSize="small" sx={{ color: "#d1180a" }} />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </div>
                            </ListItem>
                          ))}
                      </List>
                    </div>
                  </div>
                ),
              })}
            </div>
          </div>

          <AddMoneyDialog
            open={this.state.showAddDialog}
            accent={this.accent}
            isLoading={this.state.isLoading}
            onClose={() => this.setState({ showAddDialog: false })}
            onSubmit={this.handleAddTransaction}
          />

          <WithdrawMoneyDialog
            open={this.state.showSubtractDialog}
            accent={this.accent}
            isLoading={this.state.isLoading}
            onClose={() => this.setState({ showSubtractDialog: false })}
            onSubmit={this.handleWithdrawTransaction}
          />
        </div>
      </div>
    );
  }
}
